import { lazy, Suspense } from 'react';
import type { ReactNode } from 'react';
import type { RouteObject } from 'react-router-dom';

import { ClinicOnly } from './components/ClinicOnly';

import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { ROUTE_CHECKS } from '@/components/layout/admin-nav';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import { RouteFallback } from '@/components/RouteFallback';

const ClinicsPage = lazy(() => import('./pages/ClinicsPage'));
const NewClinicPage = lazy(() => import('./pages/NewClinicPage'));
const ClinicFramePage = lazy(() => import('./pages/ClinicFramePage'));
const ClinicOverviewPage = lazy(() => import('./pages/ClinicOverviewPage'));
const ClinicUsersTab = lazy(() => import('./pages/ClinicUsersTab'));
const ClinicEmrInstancesTab = lazy(() => import('./pages/ClinicEmrInstancesTab'));
const ClinicIdis2goLinksTab = lazy(() => import('./pages/ClinicIdis2goLinksTab'));
const ClinicStatusTab = lazy(() => import('./pages/ClinicStatusTab'));

function withSuspense(node: ReactNode): ReactNode {
  return <Suspense fallback={<RouteFallback />}>{node}</Suspense>;
}

/** A tab only a clinic has. The platform's url for it goes back to Overview. */
function clinicOnly(node: ReactNode): ReactNode {
  return withSuspense(<ClinicOnly>{node}</ClinicOnly>);
}

/**
 * Clinic routes, nested under the admin layout in `src/routes.tsx`.
 * `/clinics/:tenantId` holds ids only. Never a grant token (ADR-0023).
 */
export const tenantsRoutes: RouteObject[] = [
  {
    path: 'clinics',
    element: withSuspense(
      <ProtectedRoute requires={ROUTE_CHECKS.clinics}>
        <ClinicsPage />
      </ProtectedRoute>,
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: 'clinics/new',
    element: withSuspense(
      <ProtectedRoute requires={[...ROUTE_CHECKS.clinics, { action: 'create', subject: 'Tenant' }]}>
        <NewClinicPage />
      </ProtectedRoute>,
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    // The frame decides access itself: own tenant, live grant, or step-up.
    path: 'clinics/:tenantId',
    element: withSuspense(<ClinicFramePage />),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: withSuspense(<ClinicOverviewPage />),
      },
      {
        path: 'users',
        element: withSuspense(<ClinicUsersTab />),
      },
      {
        path: 'emr-instances',
        element: clinicOnly(<ClinicEmrInstancesTab />),
      },
      {
        path: 'idis2go-links',
        element: clinicOnly(<ClinicIdis2goLinksTab />),
      },
      {
        path: 'status',
        element: clinicOnly(<ClinicStatusTab />),
      },
    ],
  },
];
